// MapToolbar.jsx with improved AOI handling
import React, { useRef, useState, useEffect } from 'react';
import L from 'leaflet';
import { Layers, Pencil, Upload, ZoomIn, ZoomOut, Square, Edit, Trash2 } from 'lucide-react';
import StraightenIcon from '@mui/icons-material/Straighten';
import 'leaflet.polylinemeasure/Leaflet.PolylineMeasure.css';
import { kml } from '@tmcw/togeojson';
import shp from 'shpjs';


const buttonStyle = {
  background: 'none',
  border: 'none',
  borderBottom: '1px solid rgba(255,255,255,0.2)',
  padding: '10px',
  cursor: 'pointer',
  color: 'white',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative'
};

const activeButtonStyle = {
  ...buttonStyle,
  background: 'rgba(255,255,255,0.2)',
};

const tooltipStyle = {
  position: 'absolute',
  backgroundColor: 'rgba(0, 0, 0, 0.75)',
  color: 'white',
  padding: '5px 8px',
  borderRadius: '4px',
  fontSize: '12px',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  zIndex: 1001,
  opacity: 0,
  transition: 'opacity 0.2s',
  visibility: 'hidden'
};

const MapToolbar = ({ 
onResetFilters,
  map, 
  baseLayers = {}, 
  activeLayer, 
  setActiveLayer, 
  drawMode, 
  setDrawMode,
  position = 'left',
  toolbarStyle = {},
  onAOIDeleted,
  onDrawToolbarToggle
}) => {
  const [showLayerSelect, setShowLayerSelect] = useState(false);
  const [showDrawOptions, setShowDrawOptions] = useState(false);
  const [measurementActive, setMeasurementActive] = useState(false);
  const [cursorTooltip, setCursorTooltip] = useState({
    display: false,
    text: '',
    x: 0,
    y: 0
  });
  
  const fileInputRef = useRef(null);
  const measurePolylineRef = useRef(null);
  const measurePointsRef = useRef([]);
  const measureMarkersRef = useRef([]);
  
  // Refs for tooltip targets
  const layersButtonRef = useRef(null);
  const measureButtonRef = useRef(null);
  const drawButtonRef = useRef(null);
  const uploadButtonRef = useRef(null);
  const zoomInButtonRef = useRef(null);
  const zoomOutButtonRef = useRef(null);
  const uploadedLayersRef = useRef([]);

  const clearUploadedLayers = () => {
    uploadedLayersRef.current.forEach(layer => {
      if (layer && map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
    });
    uploadedLayersRef.current = [];
  };
  
  

  // Add custom CSS for the measurement tooltip
  useEffect(() => {
    // Add custom CSS for the tooltip
    const style = document.createElement('style');
    style.textContent = `
      .distance-tooltip {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
      }
      .distance-tooltip .leaflet-tooltip-content {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
      }
      .leaflet-tooltip-top:before {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
    
    // Clean up
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Show/hide tooltip functions
  const showTooltip = (e) => {
    const tooltip = e.currentTarget.querySelector('.tooltip');
    if (tooltip) {
      tooltip.style.opacity = '1';
      tooltip.style.visibility = 'visible';
      
      if (position === 'left') {
        tooltip.style.left = '45px';
        tooltip.style.right = 'auto';
      } else {
        tooltip.style.right = '45px';
        tooltip.style.left = 'auto';
      }
    }
  };

  const hideTooltip = (e) => {
    const tooltip = e.currentTarget.querySelector('.tooltip');
    if (tooltip) {
      tooltip.style.opacity = '0';
      tooltip.style.visibility = 'hidden';
    }
  };

  // Function to calculate distance between two points in meters
  const calculateDistance = (latlng1, latlng2) => {
    return map.distance(latlng1, latlng2);
  };

  // Format distance for display
  const formatDistance = (distanceInMeters) => {
    if (distanceInMeters < 1000) {
      return `${distanceInMeters.toFixed(1)} m`;
    } else {
      return `${(distanceInMeters / 1000).toFixed(2)} km`;
    }
  };

  // Hide cursor tooltip - separate from cleanup to avoid redundancy
  const hideCursorTooltip = () => {
    setCursorTooltip({
      display: false,
      text: '',
      x: 0,
      y: 0
    });
  };

  // Handle map click for measuring
  const handleMapClick = (e) => {
    // Only check if the polyline exists - don't check measurementActive 
    // since state updates are asynchronous and might not be updated yet
    if (!measurePolylineRef.current) {
      return;
    }
    
    const newPoint = e.latlng;
    
    // If we already have two points, clear everything
    if (measurePointsRef.current.length === 2) {
      // Remove existing polyline
      if (measurePolylineRef.current) {
        map.removeLayer(measurePolylineRef.current);
      }
      
      // Remove existing markers
      measureMarkersRef.current.forEach(marker => {
        map.removeLayer(marker);
      });
      
      // Reset collections
      measurePointsRef.current = [];
      measureMarkersRef.current = [];
      
      // Create new polyline
      measurePolylineRef.current = L.polyline([], {
        color: '#2E90FA',
        weight: 3,
        opacity: 0.8,
        dashArray: '5, 5',
        lineCap: 'round'
      }).addTo(map);
    }
    
    // Add the new point
    measurePointsRef.current.push(newPoint);
    
    // Add a marker
    const marker = L.circleMarker(newPoint, {
      radius: 4,
      color: '#2E90FA',
      fillColor: '#2E90FA',
      fillOpacity: 1,
      weight: 2
    }).addTo(map);
    measureMarkersRef.current.push(marker);
    
    // Update the polyline
    if (measurePolylineRef.current) {
      measurePolylineRef.current.setLatLngs(measurePointsRef.current);
    }
    
    // If this is the second point, complete the measurement
    if (measurePointsRef.current.length === 2) {
      // Remove the dashed style
      if (measurePolylineRef.current) {
        measurePolylineRef.current.setStyle({
          dashArray: null,
          weight: 4
        });
      }
      
      // Calculate distance
      const point1 = measurePointsRef.current[0];
      const point2 = measurePointsRef.current[1];
      const distance = calculateDistance(point1, point2);
      const formattedDistance = formatDistance(distance);
      
      // Create a midpoint for the label
      const midpoint = L.latLng(
        (point1.lat + point2.lat) / 2,
        (point1.lng + point2.lng) / 2
      );
      
      // Add a tooltip with the distance
      const tooltip = L.tooltip({
        permanent: true,
        direction: 'top',
        className: 'distance-tooltip',
        offset: [0, -10],
        opacity: 1.0
      })
      .setLatLng(midpoint)
      .setContent(`<div style="background-color: rgba(33, 150, 243, 0.8); color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;">${formattedDistance}</div>`)
      .addTo(map);
      
      measureMarkersRef.current.push(tooltip);
      
      // Hide the cursor tooltip
      hideCursorTooltip();
    }
  };

  // Handle mouse move for live preview
  const handleMouseMove = (e) => {
    // Only check if the polyline exists - don't check measurementActive
    // since state updates are asynchronous and might not be updated yet
    if (!measurePolylineRef.current) {
      return;
    }
    
    if (measurePointsRef.current.length === 1) {
      // Create a temporary array with the first point and mouse position
      const tempPoints = [measurePointsRef.current[0], e.latlng];
      measurePolylineRef.current.setLatLngs(tempPoints);
      
      // Calculate the distance
      const distance = calculateDistance(tempPoints[0], tempPoints[1]);
      const formattedDistance = formatDistance(distance);
      
      // Update cursor tooltip state
      setCursorTooltip({
        display: true,
        text: formattedDistance,
        x: e.containerPoint.x + 15,
        y: e.containerPoint.y - 15
      });
    }
  };

  // Toggle measurement mode on/off
  const toggleMeasurement = () => {
    if (measurementActive) {
      // Turn off measurement mode - the useEffect will handle cleanup
      setMeasurementActive(false);
      setDrawMode(null);
    } else {
      // Turn on measurement mode - the useEffect will handle setup
      setMeasurementActive(true);
      setDrawMode('measure');
      
      // Close other menus
      setShowLayerSelect(false);
      setShowDrawOptions(false);
    }
  };

  const handleLayers = () => {
    // Close draw options if open
    if (showDrawOptions) {
      setShowDrawOptions(false);
    }
    setShowLayerSelect(!showLayerSelect);
  };

  const handleDrawButton = () => {
    // Close layer select if open
    if (showLayerSelect) {
      setShowLayerSelect(false);
    }
    const newShowDrawOptions = !showDrawOptions;
    setShowDrawOptions(newShowDrawOptions);
    
    // Notify parent when draw toolbar is opened/closed
    if (onDrawToolbarToggle) {
      onDrawToolbarToggle(newShowDrawOptions);
    }
    
    if (drawMode) {
      disableAllDrawModes();
    }
  };

  const disableAllDrawModes = () => {
    if (!map) return;
    map.pm.disableDraw();
    map.pm.disableGlobalEditMode();
    map.pm.disableGlobalRemovalMode();
    
    // Also clean up measurement if active, but don't call toggleMeasurement
    // which can cause state timing issues
    if (measurementActive) {
      setMeasurementActive(false);
    }
    
    setDrawMode(null);
  };


useEffect(() => {
  if (!map || !onResetFilters) return;

  // Fires after user finishes drawing rectangle or polygon
  const handleShapeCreated = (e) => {
    if (e.layer instanceof L.Polygon || e.layer instanceof L.Rectangle) {
      onResetFilters();   // 🔥 Reset filters AFTER draw completes
    }
  };

  map.on("pm:create", handleShapeCreated);

  return () => {
    map.off("pm:create", handleShapeCreated);
  };
}, [map, onResetFilters]);

  const handleDrawMode = (mode) => {
    if (!map) return;
//    if (mode === 'polygon' || mode === 'rectangle') {
//      onResetFilters();   // 🔥 Trigger PanelContent reset
//    }
    
    disableAllDrawModes();
    
    switch(mode) {
      case 'rectangle':
        map.pm.enableDraw('Rectangle');
        setDrawMode('rectangle');
        break;
      case 'polygon':
        map.pm.enableDraw('Polygon');
        setDrawMode('polygon');
        break;
      case 'edit':
        map.pm.enableGlobalEditMode();
        setDrawMode('edit');
        break;
      case 'finishEdit':
        // Before disabling the edit mode, get the updated coordinates from active AOI polygon
        if (map && drawMode === 'edit') {
          // Find the active AOI polygon by looking for polygons with the specific color
          let updatedCoordinates = null;
          map.eachLayer((layer) => {
            // Check if it's a polygon with our AOI styling
            if (layer instanceof L.Polygon && 
                layer.options && 
                layer.options.color === '#2E90FA' && 
                layer.options.fillOpacity === 0) {
              
              // Get the updated coordinates
              const latLngs = layer.getLatLngs()[0];
              updatedCoordinates = latLngs.map(latLng => ({
                lat: latLng.lat,
                lng: latLng.lng
              }));
            }
          });
          
          // If we found updated coordinates, store them
          if (updatedCoordinates) {
            // Trigger a custom event that ExplorePage can listen for
            const customEvent = new CustomEvent('aoiCoordinatesUpdated', {
              detail: { coordinates: updatedCoordinates }
            });
            window.dispatchEvent(customEvent);
          }
        }
        
        // Disable edit mode, which will trigger pm:globaleditmodetoggled event
        map.pm.disableGlobalEditMode();
        setDrawMode(null);
        break;
      case 'delete':
        map.pm.enableGlobalRemovalMode();
        setDrawMode('delete');
        
        // Set up a one-time listener for the next removal
        const removedHandler = () => {
          // Call the parent component's callback when AOI is deleted
          if (onAOIDeleted && typeof onAOIDeleted === 'function') {
            onAOIDeleted();
          }
          
          // Remove this listener after it's triggered once
          map.off('pm:remove', removedHandler);
        };
        
        map.on('pm:remove', removedHandler);
        break;
      default:
        break;
    }
    setShowDrawOptions(false);
  };
  

  const switchLayer = (layerId) => {
    if (!map) return;
    
    // Remove current active layer
    if (baseLayers[activeLayer]) {
      baseLayers[activeLayer].remove();
    }
    
    // Add new layer
    if (baseLayers[layerId]) {
      baseLayers[layerId].addTo(map);
      setActiveLayer(layerId);
    }
    
    setShowLayerSelect(false);
  };

  const handleUpload = () => {
    // Close all open dropdowns
    setShowLayerSelect(false);
    setShowDrawOptions(false);
    
    if (!map || !fileInputRef.current) return;
    fileInputRef.current.click();
  };

  // Helper function to parse WKT to GeoJSON
  const parseWKT = (wktString) => {
    const trimmed = wktString.trim();
    
    // Handle POLYGON format: POLYGON((lon lat, lon lat, ...))
    if (trimmed.toUpperCase().startsWith('POLYGON')) {
      const match = trimmed.match(/POLYGON\s*\(\s*\((.*?)\)\s*\)/i);
      if (!match) {
        throw new Error('Invalid POLYGON WKT format');
      }
      
      const coordsString = match[1];
      const coordPairs = coordsString.split(',').map(s => s.trim());
      
      const coordinates = coordPairs.map(pair => {
        const parts = pair.trim().split(/\s+/);
        if (parts.length < 2) {
          throw new Error('Invalid coordinate pair in WKT');
        }
        const lon = parseFloat(parts[0]);
        const lat = parseFloat(parts[1]);
        if (isNaN(lon) || isNaN(lat)) {
          throw new Error('Invalid coordinate values in WKT');
        }
        return [lon, lat];
      });
      
      // Validate minimum 3 points (triangle)
      if (coordinates.length < 3) {
        throw new Error('Polygon must have at least 3 points (triangle)');
      }
      
      // Ensure polygon is closed (first point = last point)
      if (coordinates.length > 0) {
        const first = coordinates[0];
        const last = coordinates[coordinates.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) {
          coordinates.push([first[0], first[1]]);
        }
      }
      
      return {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [coordinates]
        },
        properties: {}
      };
    }
    
    // Handle MULTIPOLYGON format (only if it contains a single polygon)
    if (trimmed.toUpperCase().startsWith('MULTIPOLYGON')) {
      const match = trimmed.match(/MULTIPOLYGON\s*\((.*?)\)/is);
      if (!match) {
        throw new Error('Invalid MULTIPOLYGON WKT format');
      }
      
      const polygonsString = match[1];
      const polygons = [];
      let depth = 0;
      let currentPolygon = '';
      
      for (let i = 0; i < polygonsString.length; i++) {
        const char = polygonsString[i];
        if (char === '(') {
          depth++;
          if (depth > 1) currentPolygon += char;
        } else if (char === ')') {
          depth--;
          if (depth > 0) {
            currentPolygon += char;
          } else if (depth === 0 && currentPolygon) {
            // Parse this polygon
            const polygonMatch = currentPolygon.match(/\((.*?)\)/);
            if (polygonMatch) {
              const coordsString = polygonMatch[1];
              const coordPairs = coordsString.split(',').map(s => s.trim());
              const coordinates = coordPairs.map(pair => {
                const parts = pair.trim().split(/\s+/);
                const lon = parseFloat(parts[0]);
                const lat = parseFloat(parts[1]);
                return [lon, lat];
              });
              
              // Validate minimum 3 points
              if (coordinates.length < 3) {
                throw new Error('Polygon must have at least 3 points (triangle)');
              }
              
              // Close polygon
              if (coordinates.length > 0) {
                const first = coordinates[0];
                const last = coordinates[coordinates.length - 1];
                if (first[0] !== last[0] || first[1] !== last[1]) {
                  coordinates.push([first[0], first[1]]);
                }
              }
              
              polygons.push([coordinates]);
            }
            currentPolygon = '';
          }
        } else if (depth > 0) {
          currentPolygon += char;
        }
      }
      
      // Only allow single polygon in MULTIPOLYGON
      if (polygons.length > 1) {
        throw new Error('Multiple polygons found. Please upload a file with only one polygon.');
      }
      
      if (polygons.length === 0) {
        throw new Error('No valid polygon found in MULTIPOLYGON');
      }
      
      // Convert single polygon from MultiPolygon to Polygon
      return {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: polygons[0]
        },
        properties: {}
      };
    }
    
    throw new Error('Unsupported WKT geometry type. Only POLYGON and MULTIPOLYGON (with single polygon) are supported.');
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!map || !file) return;
  
    try {
      const filename = file.name.toLowerCase();
      let geojsonData;
  
      if (filename.endsWith('.geojson') || filename.endsWith('.json')) {
        const content = await file.text();
        geojsonData = JSON.parse(content);
      } else if (filename.endsWith('.kml')) {
        const text = await file.text();
        const parser = new DOMParser();
        const kmlDoc = parser.parseFromString(text, 'text/xml');
        geojsonData = kml(kmlDoc);
      } else if (filename.endsWith('.zip')) {
        const buffer = await file.arrayBuffer();
        geojsonData = await shp(buffer);
      } else if (filename.endsWith('.wkt') || filename.endsWith('.txt')) {
        // Check if it's WKT format by reading first few characters
        const content = await file.text();
        const trimmed = content.trim().toUpperCase();
        if (trimmed.startsWith('POLYGON') || trimmed.startsWith('MULTIPOLYGON') || 
            trimmed.startsWith('POINT') || trimmed.startsWith('LINESTRING')) {
          geojsonData = parseWKT(content);
      } else {
          alert("File doesn't appear to be in WKT format.");
          return;
        }
      } else {
        alert("Unsupported file type. Supported formats: GeoJSON, KML, Shapefile (ZIP), WKT");
        return;
      }
  
      // ✅ Validate and extract polygon from GeoJSON
      let validPolygon = null;
      let polygonCount = 0;

      // Handle FeatureCollection
      if (geojsonData.type === 'FeatureCollection') {
        const features = geojsonData.features || [];
        for (const feature of features) {
          if (feature.geometry && feature.geometry.type === 'Polygon') {
            polygonCount++;
            if (polygonCount === 1) {
              validPolygon = feature.geometry;
            }
          } else if (feature.geometry && feature.geometry.type === 'MultiPolygon') {
            // Count polygons in MultiPolygon
            const polygons = feature.geometry.coordinates || [];
            polygonCount += polygons.length;
            if (polygonCount === 1 && polygons.length === 1) {
              // Convert MultiPolygon with single polygon to Polygon
              validPolygon = {
                type: 'Polygon',
                coordinates: polygons[0]
              };
            }
          }
        }
      } 
      // Handle single Feature
      else if (geojsonData.type === 'Feature') {
        if (geojsonData.geometry && geojsonData.geometry.type === 'Polygon') {
          polygonCount = 1;
          validPolygon = geojsonData.geometry;
        } else if (geojsonData.geometry && geojsonData.geometry.type === 'MultiPolygon') {
          const polygons = geojsonData.geometry.coordinates || [];
          polygonCount = polygons.length;
          if (polygons.length === 1) {
            validPolygon = {
              type: 'Polygon',
              coordinates: polygons[0]
            };
          }
        }
      }
      // Handle direct Geometry
      else if (geojsonData.type === 'Polygon') {
        polygonCount = 1;
        validPolygon = geojsonData;
      } else if (geojsonData.type === 'MultiPolygon') {
        const polygons = geojsonData.coordinates || [];
        polygonCount = polygons.length;
        if (polygons.length === 1) {
          validPolygon = {
            type: 'Polygon',
            coordinates: polygons[0]
          };
        }
      }

      // Validation checks
      if (polygonCount === 0) {
        alert('No valid polygon found in the file. Please ensure the file contains a POLYGON geometry.');
        return;
      }

      if (polygonCount > 1) {
        alert(`Multiple polygons found (${polygonCount}). Please upload a file with only one polygon.`);
        return;
      }

      if (!validPolygon || !validPolygon.coordinates || !validPolygon.coordinates[0]) {
        alert('Invalid polygon geometry. Please check the file format.');
        return;
      }

      // Validate polygon ring (must have at least 3 points and be closed)
      const ring = validPolygon.coordinates[0];
      if (ring.length < 4) { // 4 because first point = last point for closed polygon
        alert('Polygon must have at least 3 points (triangle). Current polygon has fewer points.');
        return;
      }

      // Check if polygon is closed (first point = last point)
      const firstPoint = ring[0];
      const lastPoint = ring[ring.length - 1];
      const isClosed = Math.abs(firstPoint[0] - lastPoint[0]) < 0.000001 && 
                       Math.abs(firstPoint[1] - lastPoint[1]) < 0.000001;

      if (!isClosed) {
        // Auto-close the polygon
        ring.push([firstPoint[0], firstPoint[1]]);
      }

      // Validate minimum 3 unique points (triangle)
      const uniquePoints = new Set();
      for (let i = 0; i < ring.length - 1; i++) { // Exclude last point (duplicate of first)
        const point = ring[i];
        const key = `${point[0].toFixed(6)},${point[1].toFixed(6)}`;
        uniquePoints.add(key);
      }

      if (uniquePoints.size < 3) {
        alert('Polygon must have at least 3 unique points (triangle). Some points may be duplicates.');
        return;
      }

      // Convert to format expected by the rest of the application
      const coordinates = ring.slice(0, -1).map(coord => ({ // Remove duplicate last point
        lat: coord[1],
        lng: coord[0]
      }));
  
      // ✅ Remove all shape-related layers
      map.eachLayer((layer) => {
        if (layer instanceof L.Polygon || 
            layer instanceof L.Polyline || 
            layer instanceof L.Marker || 
            layer instanceof L.Circle || 
            layer instanceof L.CircleMarker || 
            layer instanceof L.GeoJSON) {
          map.removeLayer(layer);
        }
      });
  
      // ✅ Add validated polygon to map
      const validGeoJSON = {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [ring]
        },
        properties: {}
      };

      const geojsonLayer = L.geoJSON(validGeoJSON).addTo(map);
      map.fitBounds(geojsonLayer.getBounds());

      // Dispatch coordinates update event
    const customEvent = new CustomEvent('aoiCoordinatesUpdated', {
      detail: { coordinates }
    });
    window.dispatchEvent(customEvent);

  
    } catch (error) {
      console.error('Error reading file:', error);
      alert('Failed to load the file.');
    }
  };
  
  
  
  

  const handleZoomIn = () => {
    // Close all open dropdowns
    setShowLayerSelect(false);
    setShowDrawOptions(false);
    
    if (map && map.getZoom() !== undefined) {
      map.setZoom(map.getZoom() + 1);
    }
  };

  const handleZoomOut = () => {
    // Close all open dropdowns
    setShowLayerSelect(false);
    setShowDrawOptions(false);
    
    if (map && map.getZoom() !== undefined) {
      map.setZoom(map.getZoom() - 1);
    }
  };


    useEffect(() => {
      if (!map) return;

      const handleEditEnd = (e) => {
        e.layer.eachLayer((layer) => {
          if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
            const latLngs = layer.getLatLngs()[0];
            const coordinates = latLngs.map(latLng => ({
              lat: latLng.lat,
              lng: latLng.lng
            }));

            console.log('✏️ AOI edited:', coordinates);
            window.dispatchEvent(
              new CustomEvent('aoiCoordinatesUpdated', { detail: { coordinates } })
            );
          }
        });
      };

      map.on('pm:edit', handleEditEnd);

      return () => map.off('pm:edit', handleEditEnd);
    }, [map]);


  useEffect(() => {
      if (!map) return;

      // Handler for completed draw event
      const handleDrawEnd = (e) => {
        const layer = e.layer;
        if (!layer || !(layer instanceof L.Polygon || layer instanceof L.Rectangle)) return;

        // Extract coordinates
        const latLngs = layer.getLatLngs()[0];
        const coordinates = latLngs.map(latLng => ({
          lat: latLng.lat,
          lng: latLng.lng
        }));

        console.log('🟩 AOI drawn:', coordinates);

        // Dispatch global event so PanelContent can react
        window.dispatchEvent(
          new CustomEvent('aoiCoordinatesUpdated', { detail: { coordinates } })
        );

        // Optionally fit bounds or zoom to the AOI
        map.fitBounds(layer.getBounds());
      };

      // Register once when component mounts
      map.on('pm:create', handleDrawEnd);

      // Clean up
      return () => {
        map.off('pm:create', handleDrawEnd);
      };
    }, [map]);


  // Single consolidated effect to handle event listeners
  useEffect(() => {
    if (!map) return;
    
    // Clean up function to ensure proper cleanup
    const cleanup = () => {
      map.off('click', handleMapClick);
      map.off('mousemove', handleMouseMove);
      
      if (measurePolylineRef.current) {
        map.removeLayer(measurePolylineRef.current);
        measurePolylineRef.current = null;
      }
      
      // Remove all markers
      measureMarkersRef.current.forEach(marker => {
        if (marker) map.removeLayer(marker);
      });
      
      measurePointsRef.current = [];
      measureMarkersRef.current = [];
    };
    
    // If measurement is active, add event listeners
    if (measurementActive) {
      // Clean up first to ensure no duplicates
      cleanup();
      
      // Create a new polyline
      measurePolylineRef.current = L.polyline([], {
        color: '#2E90FA',
        weight: 3,
        opacity: 0.8,
        dashArray: '5, 5',
        lineCap: 'round'
      }).addTo(map);
      
      map.on('click', handleMapClick);
      map.on('mousemove', handleMouseMove);
    } else {
      // Clean up when measurement is deactivated
      cleanup();
    }
    
    // Return the cleanup function for component unmount
    return cleanup;
  }, [map, measurementActive]);

  const defaultPosition = {
    position: 'fixed',
    [position]: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgb(33, 150, 243)',
    width: '40px',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1000,
    ...toolbarStyle
  };

  return (
    <>
      <input 
        type="file" 
        ref={fileInputRef} 
        style={{ display: 'none' }} 
        onChange={handleFileChange}
        accept=".geojson,.kml,.gpx,.shp,.zip,.wkt,.txt"
      />
      <div style={defaultPosition}>
        {/* Draw Tools */}
        <div style={{ position: 'relative' }}>
          <button 
            ref={drawButtonRef}
            onClick={handleDrawButton}
            style={drawMode && !measurementActive ? activeButtonStyle : buttonStyle}
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
          >
            <Pencil size={18} />
            <div className="tooltip" style={tooltipStyle}>Draw Tools</div>
          </button>
          {showDrawOptions && (
            <div style={{
              position: 'absolute',
              [position === 'left' ? 'left' : 'right']: '100%',
              top: 0,
              marginLeft: position === 'left' ? '10px' : '-130px',
              backgroundColor: 'rgb(33, 150, 243)',
              borderRadius: '4px',
              padding: '4px',
              width: '120px'
            }}>
              <button onClick={() => handleDrawMode('rectangle')} style={drawMode === 'rectangle' ? activeButtonStyle : buttonStyle}>
                <Square size={14} /> Rectangle
              </button>
              <button onClick={() => handleDrawMode('polygon')} style={drawMode === 'polygon' ? activeButtonStyle : buttonStyle}>
                <Pencil size={14} /> Polygon
              </button>
              {/* Edit button hidden - editing disabled */}
              {/* <button onClick={() => handleDrawMode('edit')} style={drawMode === 'edit' ? activeButtonStyle : buttonStyle}>
                <Edit size={14} /> Edit
              </button> */}
            </div>
          )}
        </div>
  
        {/* Upload GeoJSON */}
        <button 
          ref={uploadButtonRef}
          onClick={handleUpload}
          style={buttonStyle}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
        >
          <Upload size={18} />
          <div className="tooltip" style={tooltipStyle}>Upload AOI</div>
        </button>
  
        {/* Map Layers */}
        <div style={{ position: 'relative' }}>
          <button 
            ref={layersButtonRef}
            onClick={handleLayers}
            style={showLayerSelect ? activeButtonStyle : buttonStyle}
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
          >
            <Layers size={18} />
            <div className="tooltip" style={tooltipStyle}>Map Layers</div>
          </button>
          {showLayerSelect && (
            <div style={{
              position: 'absolute',
              [position === 'left' ? 'left' : 'right']: '100%',
              top: 0,
              marginLeft: position === 'left' ? '10px' : '-130px',
              backgroundColor: 'rgb(33, 150, 243)',
              borderRadius: '4px',
              padding: '4px',
              width: '120px'
            }}>
              {Object.keys(baseLayers).map((layerId) => (
                <button
                  key={layerId}
                  onClick={() => switchLayer(layerId)}
                  style={{
                    background: layerId === activeLayer ? 'rgba(255,255,255,0.2)' : 'none',
                    border: 'none',
                    padding: '8px 12px',
                    color: 'white',
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {layerId}
                </button>
              ))}
            </div>
          )}
        </div>
  
        {/* Measure Distance */}
        <button 
          ref={measureButtonRef}
          onClick={toggleMeasurement}
          style={measurementActive ? activeButtonStyle : buttonStyle}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
        >
          <StraightenIcon style={{ fontSize: '18px' }} />
          <div className="tooltip" style={tooltipStyle}>Measure Distance</div>
        </button>
  
        {/* Zoom In */}
        <button 
          ref={zoomInButtonRef}
          onClick={handleZoomIn}
          style={buttonStyle}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
        >
          <ZoomIn size={18} />
          <div className="tooltip" style={tooltipStyle}>Zoom In</div>
        </button>
  
        {/* Zoom Out */}
        <button 
          ref={zoomOutButtonRef}
          onClick={handleZoomOut}
          style={{
            ...buttonStyle,
            borderBottom: 'none'
          }}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
        >
          <ZoomOut size={18} />
          <div className="tooltip" style={tooltipStyle}>Zoom Out</div>
        </button>
      </div>
  
      {/* Cursor tooltip for distance measurement */}
      {cursorTooltip.display && (
        <div style={{
          position: 'absolute',
          left: `${cursorTooltip.x}px`,
          top: `${cursorTooltip.y}px`,
          backgroundColor: 'rgb(33, 150, 243)',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold',
          pointerEvents: 'none',
          zIndex: 1000,
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          {cursorTooltip.text}
        </div>
      )}
  
      {/* Finish Edit Floating Button */}
      {drawMode === 'edit' && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgb(76, 175, 80)',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '4px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
          cursor: 'pointer',
          zIndex: 1500,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontWeight: 'bold'
        }}
        onClick={() => handleDrawMode('finishEdit')}
        >
          <Edit size={16} /> Finish Editing
        </div>
      )}
    </>
  );
};
export default MapToolbar;